import ClarityLogo from "@/assets/clarity-logo-light.svg";
import {
  BOM_PRINT_ACCENT,
  BOM_PRINT_FONT,
  BOM_PRINT_LABEL,
  BOM_PRINT_META_FONT_SIZE_PX,
  BOM_PRINT_META_LINE_HEIGHT,
  BOM_PRINT_TEXT,
} from "@/lib/bomPrintDesign";
import {
  PDF_QUOTATION_LOGO_CLASS,
  PDF_QUOTATION_LOGO_HEIGHT_PX,
  PDF_QUOTATION_LOGO_STYLE,
} from "@/lib/proposalPdfQuotationLayout";

export type DtoolsPdfQuotationHeaderInfo = {
  customerName: string;
  address?: string | null;
  proposalRef: string;
  revisionNumber?: number | null;
  proposalDate?: string | null;
  /** Optional compact mode for continuation pages. */
  compact?: boolean;
};

type Props = {
  info: DtoolsPdfQuotationHeaderInfo;
  /** Extra title line below the gold rule (e.g. option name). */
  sectionTitle?: string | null;
  testId?: string;
};

/** Repeating quotation header — customer/meta left, Clarity logo top-right (BOM tokens). */
export default function DtoolsPdfQuotationPageHeader({
  info,
  sectionTitle,
  testId = "dtools-pdf-quotation-header",
}: Props) {
  const revision =
    info.revisionNumber != null && Number.isFinite(Number(info.revisionNumber))
      ? Math.max(1, Math.floor(Number(info.revisionNumber)))
      : null;

  const metaSize = info.compact ? 9 : BOM_PRINT_META_FONT_SIZE_PX;

  return (
    <header
      className="pdf-keep-together"
      data-testid={testId}
      style={{ fontFamily: BOM_PRINT_FONT, color: BOM_PRINT_TEXT }}
    >
      <div className="flex items-start justify-between gap-4">
        <div
          className="min-w-0"
          style={{ fontSize: metaSize, lineHeight: BOM_PRINT_META_LINE_HEIGHT, fontWeight: 400 }}
        >
          <p>
            <span style={{ color: BOM_PRINT_LABEL }}>Customer: </span>
            <strong style={{ color: BOM_PRINT_TEXT, fontWeight: 600 }}>{info.customerName}</strong>
          </p>
          {info.address?.trim() ? (
            <p>
              <span style={{ color: BOM_PRINT_LABEL }}>Address: </span>
              <span style={{ color: BOM_PRINT_TEXT }}>{info.address.trim()}</span>
            </p>
          ) : null}
          <p>
            <span style={{ color: BOM_PRINT_LABEL }}>Proposal: </span>
            <span style={{ color: BOM_PRINT_TEXT }}>{info.proposalRef}</span>
          </p>
          {revision != null ? (
            <p>
              <span style={{ color: BOM_PRINT_LABEL }}>Revision: </span>
              <span style={{ color: BOM_PRINT_TEXT }}>{revision}</span>
            </p>
          ) : null}
          {info.proposalDate?.trim() ? (
            <p>
              <span style={{ color: BOM_PRINT_LABEL }}>Date: </span>
              <span style={{ color: BOM_PRINT_TEXT }}>{info.proposalDate.trim()}</span>
            </p>
          ) : null}
        </div>
        <img
          src={ClarityLogo}
          alt="Clarity"
          className={PDF_QUOTATION_LOGO_CLASS}
          style={PDF_QUOTATION_LOGO_STYLE}
          crossOrigin="anonymous"
          data-testid="dtools-pdf-quotation-logo"
          height={PDF_QUOTATION_LOGO_HEIGHT_PX}
        />
      </div>

      <div className="mt-3" style={{ borderTop: `2px solid ${BOM_PRINT_ACCENT}` }} />

      {sectionTitle?.trim() ? (
        <h2
          className="mt-3 font-bold uppercase text-[#1A2744]"
          data-testid="dtools-pdf-section-title"
          style={{ fontSize: 12, lineHeight: 1.25, letterSpacing: "0.06em", fontWeight: 700 }}
        >
          {sectionTitle.trim()}
        </h2>
      ) : null}
    </header>
  );
}
